// Sort utility for the publisher Games tab (3 columns: release, reviews, followers).
//
// Shares the `?sort=` URL param with other publisher tabs — when switching tabs
// the parent `transformSearchParams` strips it, so cross-tab collisions are
// resolved by the parser silently defaulting. See PublisherPageTabs.tsx.

export type PublisherGamesSortColumn =
  | "release_ts_utc"
  | "total_reviews"
  | "followers";

export type PublisherGamesSort =
  | `${PublisherGamesSortColumn}:${"asc" | "desc"}`
  | null;

export const PUBLISHER_GAMES_SORT_COLUMNS: readonly PublisherGamesSortColumn[] = [
  "release_ts_utc",
  "total_reviews",
  "followers",
] as const;

const VALID_DIRECTIONS = ["asc", "desc"] as const;

export const DEFAULT_PUBLISHER_GAMES_SORT: PublisherGamesSort = "release_ts_utc:desc";

export function parsePublisherGamesSort(
  sort: string | string[] | undefined,
): PublisherGamesSort {
  if (!sort) return DEFAULT_PUBLISHER_GAMES_SORT;
  const sortValue = Array.isArray(sort) ? sort[0] : sort;

  if (sortValue.includes(":")) {
    const [column, direction] = sortValue.split(":");
    if (
      PUBLISHER_GAMES_SORT_COLUMNS.includes(column as PublisherGamesSortColumn) &&
      VALID_DIRECTIONS.includes(direction as "asc" | "desc")
    ) {
      return `${column}:${direction}` as PublisherGamesSort;
    }
  }

  return DEFAULT_PUBLISHER_GAMES_SORT;
}

export function parsePublisherGamesSortParts(sort: PublisherGamesSort): {
  column: PublisherGamesSortColumn;
  direction: "asc" | "desc";
} {
  if (!sort) {
    return { column: "release_ts_utc", direction: "desc" };
  }
  const [column, direction] = sort.split(":") as [
    PublisherGamesSortColumn,
    "asc" | "desc",
  ];
  return {
    column: column || "release_ts_utc",
    direction: direction || "desc",
  };
}

export function togglePublisherGamesSortDirection(
  sort: PublisherGamesSort,
): PublisherGamesSort {
  const { column, direction } = parsePublisherGamesSortParts(sort);
  const next = direction === "asc" ? "desc" : "asc";
  return `${column}:${next}` as PublisherGamesSort;
}

export function createPublisherGamesSortForColumn(
  column: PublisherGamesSortColumn,
): PublisherGamesSort {
  return `${column}:desc` as PublisherGamesSort;
}
